export interface UserActivityData {
  lastActivityTime: number
  activityType: 'click' | 'keypress' | 'scroll' | 'mouse_move' | 'focus'
  target?: string
  path?: string
}

export interface UserActivityConfig {
  /** Throttle delay in milliseconds for activity tracking (default: 1000ms) */
  throttleDelay?: number
  /** Whether to track mouse movements (default: false) */
  trackMouseMove?: boolean
  /** Whether to track scroll events (default: true) */
  trackScroll?: boolean
  /** Whether to track keyboard events (default: true) */
  trackKeyboard?: boolean
  /** Whether to track click events (default: true) */
  trackClick?: boolean
  /** Whether to track focus events (default: true) */
  trackFocus?: boolean
  /** Custom callback for activity events */
  onActivity?: (data: UserActivityData) => void
  /** Minimum inactivity duration in milliseconds to trigger onInactivity callback (default: 5000ms) */
  inactivityDuration?: number
  /** Callback fired when user returns after being inactive for longer than inactivityDuration */
  onInactivity?: (inactivityDuration: number) => void
}

export class UserActivityTracker {
  private lastActivityTime = 0
  private inactiveStartTime: number | null = null
  private isPageVisible = true
  private isPageFocused = true
  private config: Required<UserActivityConfig>
  private throttleTimer: ReturnType<typeof setTimeout> | null = null
  private eventListeners: Array<[string, EventListener]> = []

  constructor(config: UserActivityConfig = {}) {
    this.config = {
      throttleDelay: 1000,
      trackMouseMove: false,
      trackScroll: true,
      trackKeyboard: true,
      trackClick: true,
      trackFocus: true,
      inactivityDuration: 5000,
      onActivity: () => {},
      onInactivity: () => {},
      ...config,
    }

    // Initialize throttled activity handler
    this.handleActivity = this.throttle((type: UserActivityData['activityType'], event?: Event) => {
      const activityData = this.createActivityData(type, event)

      // Update internal state
      this.lastActivityTime = activityData.lastActivityTime

      // Call custom callback if provided
      if (this.config.onActivity) {
        this.config.onActivity(activityData)
      }
    }, this.config.throttleDelay)

    if (typeof window === 'undefined' || typeof document === 'undefined') return

    // Initialize page state
    this.isPageVisible = !document.hidden
    this.isPageFocused = document.hasFocus()

    this.setupEventListeners()
  }

  private createActivityData(
    type: UserActivityData['activityType'],
    event?: Event,
  ): UserActivityData {
    const now = Date.now()
    let target = ''

    if (event?.target) {
      const element = event.target as HTMLElement
      // Create a simple selector for the target
      if (element?.id) {
        target = `#${element.id}`
      } else if (element?.className && typeof element.className === 'string') {
        target = `.${element.className.split(' ')[0]}`
      } else if (element?.tagName) {
        target = element.tagName.toLowerCase()
      }
    }

    return {
      lastActivityTime: now,
      activityType: type,
      target,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  private throttle<T extends (...args: any[]) => any>(
    func: T,
    delay: number,
  ): (...args: Parameters<T>) => void {
    return (...args: Parameters<T>) => {
      if (this.throttleTimer) {
        clearTimeout(this.throttleTimer)
      }
      this.throttleTimer = setTimeout(() => {
        func(...args)
        this.throttleTimer = null
      }, delay)
    }
  }

  private handleActivity: (type: UserActivityData['activityType'], event?: Event) => void

  private checkInactivity() {
    const isCurrentlyActive = this.isPageVisible && this.isPageFocused

    // User became inactive
    if (!isCurrentlyActive && this.inactiveStartTime === null) {
      this.inactiveStartTime = Date.now()
    }
    // User became active again
    else if (isCurrentlyActive && this.inactiveStartTime !== null) {
      const inactivityDuration = Date.now() - this.inactiveStartTime
      this.inactiveStartTime = null

      // Fire inactivity callback if duration exceeds threshold
      if (inactivityDuration >= this.config.inactivityDuration && this.config.onInactivity) {
        this.config.onInactivity(inactivityDuration)
      }
    }
  }

  private setupEventListeners() {
    const events: Array<[string, UserActivityData['activityType'], boolean]> = []

    if (this.config.trackClick) {
      events.push(['click', 'click', true])
    }
    if (this.config.trackKeyboard) {
      events.push(['keydown', 'keypress', true])
    }
    if (this.config.trackScroll) {
      events.push(['scroll', 'scroll', true])
    }
    if (this.config.trackMouseMove) {
      events.push(['mousemove', 'mouse_move', true])
    }
    if (this.config.trackFocus) {
      events.push(['focusin', 'focus', true])
    }

    // Add event listeners
    events.forEach(([eventType, activityType, capture]) => {
      const handler = (event: Event) => {
        this.handleActivity(activityType, event)
      }

      document.addEventListener(eventType, handler, capture)
      this.eventListeners.push([eventType, handler])
    })

    // Add inactivity tracking event listeners
    const visibilityChangeHandler = () => {
      this.isPageVisible = !document.hidden
      this.checkInactivity()
    }

    const focusHandler = () => {
      this.isPageFocused = true
      this.checkInactivity()
    }

    const blurHandler = () => {
      this.isPageFocused = false
      this.checkInactivity()
    }

    document.addEventListener('visibilitychange', visibilityChangeHandler)
    window.addEventListener('focus', focusHandler)
    window.addEventListener('blur', blurHandler)

    this.eventListeners.push(['visibilitychange', visibilityChangeHandler])
    this.eventListeners.push(['focus', focusHandler])
    this.eventListeners.push(['blur', blurHandler])
  }

  /**
   * Get the timestamp of the last recorded activity
   */
  getLastActivityTime(): number {
    return this.lastActivityTime
  }

  /**
   * Check if user was active within the specified time window
   */
  wasActiveWithin(milliseconds: number): boolean {
    if (this.lastActivityTime === 0) return false
    return Date.now() - this.lastActivityTime < milliseconds
  }

  /**
   * Check if the user is currently inactive (page not visible or not focused)
   */
  isCurrentlyInactive(): boolean {
    return !this.isPageVisible || !this.isPageFocused
  }

  /**
   * Get the current inactivity duration in milliseconds, or null if user is active
   */
  getCurrentInactivityDuration(): number | null {
    if (this.inactiveStartTime === null) return null
    return Date.now() - this.inactiveStartTime
  }

  /**
   * Destroy the tracker and clean up event listeners
   */
  destroy(): void {
    // Clear throttle timer
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer)
      this.throttleTimer = null
    }

    // Remove event listeners
    this.eventListeners.forEach(([eventType, handler]) => {
      if (eventType === 'focus' || eventType === 'blur') {
        window.removeEventListener(eventType, handler, true)
      } else {
        document.removeEventListener(eventType, handler, true)
      }
    })
    this.eventListeners = []
  }
}
